import React, { useContext, useEffect } from 'react';
import { Button, Drawer, Icon, Link } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { ResourceLocation } from '@/generated/data.schemas';
import { useGetTranslations } from '@/generated/translations';
import { stripAllWhitespace } from '@/helpers';
import { DrawerMegaMenuItem } from '@/types/drawerMegaMenuItem';
import { CorporateLink } from '../CorporateLink';
import { SegmentBar } from '../SegmentBar';
import styles from './DrawerMegaMenu.module.scss';

interface DrawerMegaMenuProps {
	megaMenu?: DrawerMegaMenuItem[];
	isMenuOpen?: boolean;
	isSalesPortal?: boolean;
	handleCloseMenu: () => void;
}

export const DrawerMegaMenu: React.FunctionComponent<DrawerMegaMenuProps> = ({
	megaMenu,
	isMenuOpen,
	isSalesPortal,
	handleCloseMenu,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const itemRef = React.useRef<(HTMLLIElement | null)[]>([]);
	// List of currently active items (or rather, path) - used for showing "pages"
	const [activeItems, setActiveItems] = React.useState<(string | HTMLLIElement | null)[]>([]);
	// List of items, that have been rendered - used for rendering full markup within items
	const [renderedItems, setRenderedItems] = React.useState<(string | HTMLLIElement | null)[]>([]);
	const [menuHasOpened, setMenuHasOpened] = React.useState(isMenuOpen);
	// Current level of menu - used for hidden/shown logic of level 1
	const [activeLevel, setActiveLevel] = React.useState(0);

	const drawerTransTime = 400; // should match value in CSS in Drawer

	useEffect(() => {
		if (isMenuOpen) {
			setActiveLevel(1);
			setMenuHasOpened(true);
		} else {
			setTimeout(() => {
				setActiveItems([]);
				setActiveLevel(0);
			}, drawerTransTime);
		}
	}, [menuHasOpened, isMenuOpen]);

	const handleItemActivation = (key, levelCount): void => {
		setActiveItems((arr) => [...arr, itemRef.current[key]]);
		setRenderedItems((arr) => [...arr, itemRef.current[key]]);
		setActiveLevel(levelCount);
	};

	const handleBack = React.useCallback(
		(toRemove): void => {
			setActiveItems(activeItems.filter((item) => item !== toRemove));
			setActiveLevel(activeLevel - 1);
		},
		[activeItems, activeLevel],
	);

	const renderLevel = (
		level: DrawerMegaMenuItem[],
		levelCount = 1,
		parentRef = null,
		parentTitle: string | null | undefined = null,
		parentInternalLink: string | undefined = undefined,
		parentExternalLink: string | undefined = undefined,
		parentRoute: ResourceLocation | undefined = undefined,
		parentShowLinkToSelector: boolean = false,
		isLevelActive?: boolean,
	): React.ReactNode => (
		<Drawer.Level isActive={isLevelActive} isUnstyled={levelCount < 2} zIndex={levelCount}>
			{renderedItems.includes(parentRef) ? (
				<Drawer.Header
					isSubLevel
					handleBack={(): void => {
						handleBack(parentRef);
					}}
					handleClose={handleCloseMenu}
				/>
			) : null}

			<Drawer.Body>
				{parentShowLinkToSelector ? <SegmentBar closeDrawer={handleCloseMenu} /> : null}

				{renderedItems.includes(parentRef) && parentTitle && (
					<h2 className={styles.level1Title}>
						{translations?.data.mobileMenu.menuHeadingPrefix + ' ' + parentTitle}
					</h2>
				)}

				<ul className={styles.list}>
					{(parentExternalLink || parentInternalLink || parentRoute?.externalRoute) && (
						<>
							{renderedItems.includes(parentRef) && parentTitle && (
								<li className={styles.itemSpacer} aria-hidden={!isLevelActive}>
									<Link
										className={styles.seeAllLink}
										to={parentInternalLink || parentRoute?.externalRoute}
										state={parentRoute}
										linkUrl={parentExternalLink}
										onClick={handleCloseMenu}
										textLink
										tabIndex={!isLevelActive ? -1 : undefined}
										// The accessible name must contain the visible "See all" text (WCAG 2.5.3 Label in Name)
										aria-label={translations?.data.mobileMenu.goToPage + ' ' + parentTitle}
									>
										{translations?.data.mobileMenu.goToPage}
									</Link>
								</li>
							)}
						</>
					)}

					{level.map((item, index) => {
						const key = stripAllWhitespace(`${levelCount}-${index}-${item.name}`);
						const isActive = activeItems.includes(itemRef.current[key]);
						const renderLevel1Full = menuHasOpened && levelCount < 2;
						const renderDescendantsFull = renderedItems.includes(parentRef);

						const getIsShown = (): boolean => {
							if (levelCount === 1) return activeLevel === 1;
							if (levelCount === activeLevel) return Boolean(isLevelActive);
							return false;
						};

						// Categories linking to the application solution builder still have the segment
						// bar to show, so they open a panel even without subcategories. Mirrors the
						// same condition in DesktopMainMenu.
						const hasPanel = item?.children?.length > 0 || Boolean(item?.showLinkToSelector);

						return (
							<li
								className={styles.itemSpacer}
								aria-hidden={!getIsShown()}
								key={key}
								ref={(ref): HTMLLIElement | null => (itemRef.current[key] = ref)}
							>
								{/* Minimize DOM elements initially */}
								{renderLevel1Full || renderDescendantsFull ? (
									<>
										{hasPanel ? (
											<Button
												className={styles.itemContentButton}
												variant={'ghost'}
												isFullWidth
												noRadius
												onClick={(): void => {
													handleItemActivation(key, levelCount + 1);
												}}
												tabIndex={!getIsShown() ? -1 : undefined}
											>
												<span>{item.name}</span>
												<Icon name={'chevronDown'} size={'md'} rotate={'270'} />
											</Button>
										) : (
											<Link
												className={styles.itemContentWithNoChildren}
												to={item?.internalLink || item?.route?.externalRoute}
												linkUrl={item?.externalLink}
												state={item?.route}
												onClick={handleCloseMenu}
												tabIndex={!getIsShown() ? -1 : undefined}
												textLink
											>
												{item.name}
											</Link>
										)}
									</>
								) : (
									<Link
										to={item?.internalLink || item?.route?.externalRoute}
										linkUrl={item?.externalLink}
										state={item?.route}
										tabIndex={!getIsShown() ? -1 : undefined}
									>
										{item.name}
									</Link>
								)}

								{hasPanel &&
									renderLevel(
										item.children ?? [],
										levelCount + 1,
										itemRef.current[key],
										item.name,
										item.internalLink,
										item.externalLink,
										item.route,
										item.showLinkToSelector,
										isActive,
									)}
							</li>
						);
					})}
				</ul>
			</Drawer.Body>
		</Drawer.Level>
	);

	return (
		<nav
			className={styles.wrapper}
			role={'navigation'}
			aria-label={`${translations?.data.shared.navigation.primary} ${translations?.data.shared.navigation.nav}`}
		>
			{megaMenu && renderLevel(megaMenu)}

			{isSalesPortal && <CorporateLink />}
		</nav>
	);
};
